var _excluded = ["spacing"],
  _excluded2 = ["isCurrentPage", "as", "className"],
  _excluded3 = ["isCurrentPage", "separator", "spacing", "children", "className"],
  _excluded4 = ["children", "spacing", "separator", "className"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import { FaChevronLeft } from 'react-icons/fa';
import { forwardRef, omitThemingProps, StylesProvider, tqc, useMultiStyleConfig, useStyles } from '../../system';
import { cx, getValidChildren, __DEV__ } from '../../utils';
/**
 * React component that separates each breadcrumb link
 */
export var BreadcrumbSeparator = forwardRef((props, ref) => {
  var {
      spacing
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  var styles = useStyles();
  var separatorStyles = _objectSpread({
    mx: spacing
  }, styles.separator);
  return /*#__PURE__*/React.createElement(tqc.span, _extends({
    ref: ref,
    role: "presentation"
  }, rest, {
    __css: separatorStyles
  }));
});
if (__DEV__) {
  BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';
}
/**
 * Breadcrumb link.
 *
 * It renders a `span` when it matches the current link. Otherwise,
 * it renders an anchor tag.
 */
export var BreadcrumbLink = forwardRef((props, ref) => {
  var {
      isCurrentPage,
      as,
      className
    } = props,
    rest = _objectWithoutProperties(props, _excluded2);
  var styles = useStyles();
  var sharedProps = _objectSpread({
    ref,
    as,
    className: cx('tqc-breadcrumb__link', className)
  }, rest);
  if (isCurrentPage) {
    return /*#__PURE__*/React.createElement(tqc.span, _extends({
      "aria-current": "page",
      __css: _objectSpread(_objectSpread({}, styles.link), {}, {
        _hover: undefined,
        cursor: 'default'
      })
    }, sharedProps));
  }
  return /*#__PURE__*/React.createElement(tqc.a, _extends({
    __css: styles.link
  }, sharedProps));
});
if (__DEV__) {
  BreadcrumbLink.displayName = 'BreadcrumbLink';
}
/**
 * BreadcrumbItem is used to group a breadcrumb link.
 * It renders a `li` element to denote it belongs to an order list of links.
 */
export var BreadcrumbItem = forwardRef((props, ref) => {
  var {
      isCurrentPage,
      separator,
      spacing,
      children,
      className
    } = props,
    rest = _objectWithoutProperties(props, _excluded3);
  var validChildren = getValidChildren(children);
  var clones = validChildren.map(child => {
    if (child.type === BreadcrumbLink) {
      return /*#__PURE__*/React.cloneElement(child, {
        isCurrentPage
      });
    }
    if (child.type === BreadcrumbSeparator) {
      return /*#__PURE__*/React.cloneElement(child, {
        spacing,
        children: child.props.children || separator
      });
    }
    return child;
  });
  var styles = useStyles();
  var itemStyles = _objectSpread({
    display: 'inline-flex',
    alignItems: 'center'
  }, styles.item);
  var _className = cx('tqc-breadcrumb__list-item', className);
  return /*#__PURE__*/React.createElement(tqc.li, _extends({
    ref: ref,
    className: _className
  }, rest, {
    __css: itemStyles
  }), /*#__PURE__*/React.createElement(BreadcrumbSeparator, {
    spacing: spacing
  }, separator), clones);
});
if (__DEV__) {
  BreadcrumbItem.displayName = 'BreadcrumbItem';
}
var ChevronLeft = tqc(FaChevronLeft);

/**
 * Breadcrumb is used to render a breadcrumb navigation landmark.
 * It renders a `nav` element with `aria-label` set to `Breadcrumb`
 */
export var Breadcrumb = forwardRef((props, ref) => {
  var styles = useMultiStyleConfig('Breadcrumb', props);
  var ownProps = omitThemingProps(props);
  var {
      children,
      spacing = 2,
      separator = /*#__PURE__*/React.createElement(ChevronLeft, {
        mb: "-0.125rem",
        color: "blue.700"
      }),
      className
    } = ownProps,
    rest = _objectWithoutProperties(ownProps, _excluded4);
  var validChildren = getValidChildren(children);
  var clones = validChildren.map(child => /*#__PURE__*/React.cloneElement(child, {
    separator,
    spacing
  }));
  var _className = cx('tqc-breadcrumb', className);
  return /*#__PURE__*/React.createElement(tqc.nav, _extends({
    ref: ref,
    "aria-label": "breadcrumb",
    className: _className,
    __css: styles.container
  }, rest), /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, /*#__PURE__*/React.createElement(tqc.ol, {
    className: "tqc-breadcrumb__list"
  }, clones)));
});
if (__DEV__) {
  Breadcrumb.displayName = 'Breadcrumb';
}